/**
 * Performance overlay: numbers that stay visible with the panel closed.
 * English only, it is a technical readout. DOM is rewritten at most four
 * times a second.
 */

export interface HudStats {
  fps: number;
  frameMs: number;
  stepsPerSec: number;
  active: number;
  requested: number;
  width: number;
  height: number;
  dpr: number;
  renderer: string;
  /** '32F' when field sums are exact, '16F' on the half-float fallback. */
  fields: string;
  simTime: number;
  falcon: string;
  alarm: number;
  debug: boolean;
}

export class Hud {
  private readonly root: HTMLElement;
  private readonly lines: HTMLElement;
  private readonly gpu: HTMLElement;
  private visible = false;
  private lastWrite = 0;

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'hud';
    this.root.hidden = true;
    this.lines = document.createElement('pre');
    this.gpu = document.createElement('div');
    this.gpu.className = 'gpu';
    this.root.append(this.lines, this.gpu);
    host.appendChild(this.root);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.root.hidden = !v;
  }

  update(now: number, s: HudStats): void {
    if (!this.visible || now - this.lastWrite < 250) return;
    this.lastWrite = now;
    const mm = Math.floor(s.simTime / 60);
    const ss = Math.floor(s.simTime % 60);
    this.lines.textContent = [
      `${s.fps.toFixed(0).padStart(3)} fps  ${s.frameMs.toFixed(1).padStart(5)} ms  ${s.stepsPerSec.toFixed(0).padStart(2)} steps/s`,
      `${s.active.toLocaleString('en')} / ${s.requested.toLocaleString('en')} birds`,
      `${s.width}×${s.height} @${s.dpr.toFixed(2)}  fields ${s.fields}`,
      `t ${mm}:${String(ss).padStart(2, '0')}  falcon ${s.falcon}  alarm ${s.alarm.toFixed(2)}${s.debug ? '  [agitation view]' : ''}`,
    ].join('\n');
    this.gpu.textContent = shortRenderer(s.renderer);
  }
}

/** "ANGLE (Apple, ANGLE Metal Renderer: Apple M2, Unspecified Version)" → "Apple, Apple M2". */
export function shortRenderer(r: string): string {
  let s = r.trim();
  const m = /^ANGLE \((.*)\)$/.exec(s);
  if (m) s = m[1]!;
  s = s
    .replace(/,?\s*Unspecified Version/i, '')
    .replace(/\s*Direct3D\d+\s+vs_\d_\d\s+ps_\d_\d/i, '')
    .replace(/ANGLE Metal Renderer:\s*/i, '')
    .replace(/\s*\(0x[0-9a-f]+\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return s;
}
